import type { StrictOmit } from '@enmon/commons';
import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { hasAuthMethod, hasIdentifiedUser, type StoredIdentifiedUser } from '../utils/identifiedUserStorage';

interface AuthGuardProps {
  children: ReactNode;
  requireIdentified?: boolean;
  requiredMethod?: keyof StrictOmit<StoredIdentifiedUser['methods'], 'oauthProviders'> | null;
}

/**
 * Guard component that ensures users follow the proper authentication flow.
 * Redirects to identify page if:
 * - User hasn't gone through identification first
 * - User doesn't have the required authentication method available
 */
export const AuthGuard: FC<AuthGuardProps> = ({ children, requireIdentified = true, requiredMethod = null }) => {
  const history = useHistory();
  const location = useLocation();
  const { identifiedUser } = useAuthContext();

  useEffect(() => {
    if (!requireIdentified) {
      return;
    }

    if (!identifiedUser) {
      return;
    }

    // Check if user has been identified (from state or session storage)
    const isIdentified = hasIdentifiedUser(identifiedUser.identifier);

    if (!isIdentified) {
      // Redirect to identify page if not identified
      history.replace({
        pathname: '/',
        search: location.search,
      });
      return;
    }

    // If a specific method is required, check if user has it
    if (requiredMethod) {
      const hasMethod = hasAuthMethod(requiredMethod, identifiedUser.methods);

      if (!hasMethod) {
        // User doesn't have the required auth method, redirect back to identify
        history.replace({
          pathname: '/',
          search: location.search,
        });
      }
    }
  }, [identifiedUser, requiredMethod, requireIdentified, history, location.search]);

  // Don't render children if requirements not met
  if (requireIdentified) {
    if (!identifiedUser) {
      return null;
    }

    const isIdentified = hasIdentifiedUser(identifiedUser.identifier);

    if (!isIdentified) {
      return null;
    }

    if (requiredMethod) {
      const hasMethod = hasAuthMethod(requiredMethod, identifiedUser.methods);
      if (!hasMethod) {
        return null;
      }
    }
  }

  return <>{children}</>;
};
